import { goto } from '$app/navigation';
import type { PartnerAddress, PatchPartnerAddress } from '@enzianweb/shared';
import { addNotification, addQueueTask, createPartnerAddress, createSimpleListConfiguration, createSimpleListListFilter, deletePartnerAddress, deletePartnerAddresses, getPartnerAddresses, handleStoreError, massPatchPartnerAddress, patchPartnerAddress, requestPartnerAddressesExport, selectedClientStore, ViewMode, type SimpleListConfiguration, type SimpleListListFilter, type SimpleListStore, type StoreListener } from '@enzianweb/shared';
import Big from "big.js";
import { _ } from "svelte-i18n";
import { get } from "svelte/store";

export const PARTNER_ROUTE = "partner-addresses"


export interface PartnerStore extends SimpleListStore {
    get totalItemCount(): number | undefined

    handlePartnerCreation: () => Promise<void>
    handlePatchPartnerAddressStore: (id: number, patchPartnerAddressObject: PatchPartnerAddress) => Promise<void>

    handleDeleteAddressStore: (id: number) => Promise<void>
    handleDeletePartnerAddressesStore: (partnerAddressesIds: number[]) => Promise<void>
    massPatchPartnerAddressStore: (partnerAddressesIds: number[], isShared: boolean) => Promise<void>
    handlePartnerAddressExportRequest: () => Promise<void>
}

export function createPartnerStore(): PartnerStore {

    let partnerAddressStore = $state<PartnerAddress[]>([]);
    let totalItemCount: number | undefined = $state()
    let storeListeners: StoreListener[] = $state([])

    //Filter for backend
    let listFilter: SimpleListListFilter = $state(
        createSimpleListListFilter({
            localStorageKey: 'partner-list',
            filterOptions: [
                {
                    label: get(_)('partnerview.table_header_partner_address_nr'),
                    value: null,
                    type: 'number',
                    key: 'partnerNr',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_firm_name'),
                    value: null,
                    type: 'string',
                    key: 'firmName'
                },
                {
                    label: get(_)('partnerview.table_header_partner_name'),
                    value: null,
                    type: 'string',
                    key: 'name'
                },
                {
                    label: get(_)('partnerview.table_header_partner_firstname'),
                    value: null,
                    type: 'string',
                    key: 'firstName',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_relation'),
                    value: null,
                    type: 'string',
                    key: 'relationTag',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_addon'),
                    value: null,
                    type: 'string',
                    key: 'addon',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_street_one'),
                    value: null,
                    type: 'string',
                    key: 'streetFirst',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_street_two'),
                    value: null,
                    type: 'string',
                    key: 'streetSecond',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_zip'),
                    value: null,
                    type: 'string',
                    key: 'zip',
                    tableOption: {
                        allowToggleVisibility: true,
                        width: 60
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_place'),
                    value: null,
                    type: 'string',
                    key: 'place',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_phone'),
                    value: null,
                    type: 'string',
                    key: 'phoneFirst',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_email'),
                    value: null,
                    type: 'string',
                    key: 'emailFirst',
                    tableOption: {
                        allowToggleVisibility: true
                    }
                },
                {
                    label: get(_)('partnerview.table_header_partner_creation_date_filter'),
                    value: null,
                    type: 'date',
                    key: 'creationDate',
                    tableOption: { allowToggleVisibility: true, },
                    orSearch: false
                },
                {
                    label: get(_)('partnerview.table_header_partner_is_shared'),
                    value: null,
                    type: 'boolean',
                    key: 'isShared',
                    tableOption: { width: 80, allowToggleVisibility: true },
                    orSearch: false
                }
            ]
        })
    );

    let listConfig: SimpleListConfiguration = $state(createSimpleListConfiguration({
        header: get(_)('partnerview.tab_group_name'),
        showEmptyAdd: false,
        showViewModeSwitches: [ViewMode.LIST, ViewMode.SMALL, ViewMode.BIG],
        tableModeConfig: {
            showTableConfig: true
        },
        cardModeConfig: {
            amountOfCols: 5
        }
    }));


    async function loadData() {
        try {
            const partnerListWrapper = await getPartnerAddresses(listFilter);

            //Set the totalItem count
            totalItemCount = partnerListWrapper.totalItemCount;

            if (listFilter.page == 0) {
                //Reset the data.
                partnerAddressStore = partnerListWrapper.data;
            } else {
                //Merge the lists
                partnerAddressStore.push(...partnerListWrapper.data)
            }

            //Increase the page counter.
            if (partnerAddressStore.length > 0) {
                listFilter.page += 1;
            }

        } catch (error) {
            handleStoreError('partnerview.err_partner_load', error);
        }
    }

    return {
        get data() { return partnerAddressStore },
        set data(value) { partnerAddressStore = value },
        get listFilter() { return listFilter },
        set listFilter(value) { listFilter = value },
        get listConfig() { return listConfig },
        set listConfig(value) { listConfig = value },
        get totalItemCount() { return totalItemCount },
         addNotifier(listener) {
            storeListeners.push(listener)
        },
        removeNotifier(listener) {
            storeListeners = storeListeners.filter((li) => li == listener)
        },
        loadData: loadData,
        async applyFilter() {
            //Reset the start id
            listFilter.page = 0;

            //Load the data.
            await loadData();

            //Inform the listeners
            storeListeners.forEach((listener) => {
                //Call the event
                listener.onFilterApplied()
            }) 
        },
        clear() {
            partnerAddressStore = []
        },
        async handlePartnerCreation() {
            try {
                //Create the new Entry
                const newPartner = await createPartnerAddress({
                    name: get(_)('partnerview.lbl_new_partner_name'),
                    streetFirst: '',
                    zip: '',
                    place: '',
                    countryCode: 'CH',
                    discount: new Big(0)
                });

                //Insert the new item
                partnerAddressStore.push(newPartner)

                //Let the user know that it worked
                addNotification({
                    message: get(_)('partnerview.lbl_create_partner_success'),
                    type: 'success'
                });

                //Navigate to the detail page.
                goto('/' + PARTNER_ROUTE + '/' + newPartner.id);

            } catch (error) {
                handleStoreError('partnerview.err_partner_create', error);
            }
        },

        async handlePatchPartnerAddressStore(id: number, patchPartnerAddressObject: PatchPartnerAddress) {

            let patchedItem = await patchPartnerAddress(id, patchPartnerAddressObject);

            const index = partnerAddressStore.findIndex(address => address.id == id)
            if (index < 0) return

            let partner = partnerAddressStore[index]

            //Check if we did set the shared flag.
            if (patchPartnerAddressObject.isShared != undefined) {

                if (partner.fkClient != get(selectedClientStore)?.id) {
                    //Remove the item from the list if it doesn't belong to this client
                    partnerAddressStore = partnerAddressStore.filter((address) => address.id != id)
                } else {
                    //Update the item in the store.
                    partnerAddressStore[index] = patchedItem
                }
            } else {
                //Update the item in the store.
                partnerAddressStore[index] = patchedItem
            }
        },
        async handleDeleteAddressStore(id: number) {
            try {
                await deletePartnerAddress(id);

                //Remove the item from the list
                partnerAddressStore = partnerAddressStore.filter(
                    (listItem) => listItem.id != id
                );

                addNotification({
                    message: get(_)("partnerview.lbl_partner_delete"),
                    type: "success",
                });
            } catch (error) {
                handleStoreError("partnerview.err_partner_delete", error);
            }
        },

        async handleDeletePartnerAddressesStore(partnerAddressesIds: number[]) {
            try {
                //Make the web call
                await deletePartnerAddresses(partnerAddressesIds)

                //Adjust the list
                partnerAddressStore = partnerAddressStore.filter(partner => !partnerAddressesIds.includes(partner.id))

            } catch (err) {
                handleStoreError("partnerview.err_partner_delete", err)
            }

        },

        async massPatchPartnerAddressStore(partnerAddressesIds: number[], isShared: boolean) {

            try {
                //Make the web call
                await massPatchPartnerAddress(partnerAddressesIds, {
                    isShared: isShared,
                })

                //Set the flag in memory
                partnerAddressStore.forEach(address => {
                    if (partnerAddressesIds.includes(address.id)) {
                        address.isShared = isShared
                    }
                })

            } catch (err) {
                handleStoreError("", err)
            }
        },
        async handlePartnerAddressExportRequest() {
            //Request the export
            const task = await requestPartnerAddressesExport(listFilter);
            //Add the task to the stack
            addQueueTask(task);
        }
    }
}

